import axios from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL } from '../../constants/system'
import { extractErrorMessage } from '../../utils/apitools'
import useAccount from '../useAccount'

/**
 * The Sky reconciliation page's data: Sky's own Sprinklr numbers beside ours
 * for any UTC date range, posts and comments, asked live by the server and
 * cached there for an hour, the comment day drill-down, and the two counting
 * switches (goapi/pkg/server/sky_reconciliation.go,
 * design/2026-10-19-sky-reconciliation-on-demand.md). Like every Sky route it
 * names no customer or job: the backend fixes both.
 */

export interface ReconMeasures {
  posts: number
  rows: number
  reach: number
  views: number
  engagements: number
}

export interface ReconCell {
  month: string
  campaignId: string
  campaign: string
  platform: string
  sky: ReconMeasures
  ours: ReconMeasures
  oursInTable: ReconMeasures
  oursAsTable: ReconMeasures
  reasons?: Record<string, number>
  /** What of Sky − Platinum (switches off) no reason accounts for. */
  residual: ReconMeasures
  explained: boolean
  imported: boolean
}

export interface ReconPost {
  reason: string
  explanation: string
  month: string
  campaignId: string
  campaign: string
  platform: string
  key: string
  url?: string
  skyRows: number
  ourTableRows: number
  skyReach: number
  ourReach: number
  skyViews: number
  ourViews: number
  skyEngagements: number
  ourEngagements: number
  ourLastReadMs?: number
  /** This post's share of Sky − Platinum with both switches off … */
  amount: ReconMeasures
  /** … with the first switch on, and with both on (absent on old reports). */
  amountInTable?: ReconMeasures
  amountAsTable?: ReconMeasures
}

/** One step of the sum from Platinum's number to Sprinklr's. */
export interface ReconBridgeLine {
  reason: string
  explanation: string
  /** How many posts the step covers. */
  count: number
  /** What it adds on the way from Platinum to Sprinklr. */
  amount: ReconMeasures
}

/**
 * Platinum + steps + notExplained = Sprinklr, exactly (sprinklrrecon.Bridge).
 * `matched` is Platinum on Sky's terms: Sprinklr less what is not explained.
 */
export interface ReconBridge {
  platinum: ReconMeasures
  steps: ReconBridgeLine[]
  notExplained: ReconBridgeLine
  sprinklr: ReconMeasures
  matched: ReconMeasures
}

export interface ReconBridges {
  switchesOff: ReconBridge
  postsInTable: ReconBridge
  countAsTable: ReconBridge
}

/** match: nothing unexplained; floaters: a few, within tolerance; unexplained: more, or sums that do not close. */
export type ReconStatus = 'match' | 'floaters' | 'unexplained'

export interface ReconReport {
  windowStartMs: number
  windowEndMs: number
  readAtMs: number
  verdict: string
  status: ReconStatus
  unexplained: number
  /** Posts with an unexplained difference (a post can have two). */
  unexplainedPosts: number
  /** sprinklrrecon.UnexplainedTolerance: up to this many such posts is not an error. */
  tolerance: number
  /** Absent on a report stored before the step-by-step sum existed. */
  bridges?: ReconBridges
  skyPosts: number
  skyRows: number
  unkeyable: number
  reasonCounts: Record<string, number>
  totals: ReconCell
  skyNotImported: ReconMeasures
  cells: ReconCell[] | null
  posts: ReconPost[]
}

export interface CountingSettings {
  postsInSprinklrTable: boolean
  countAsSprinklrTable: boolean
  updatedBy: string
  updatedAt: number
}

/** One platform's (X: one kind's) comment totals over the window (sprinklrrecon.CommentLine). */
export interface CommentLine {
  platform: string
  /** "" except X: update, reply, mention, repost, other, unclassified. */
  kind: string
  sky: number
  ours: number
  /** Of `ours`, how many have their text coded (themes, sentiment, emotion). Absent on an older server. */
  coded?: number
  imported: boolean
  /** "sky_own_post" on a platform's own_posts line (Sky's own posts): outside every total and difference. */
  reason?: string
}

/** One platform(-kind)-day where Sky and Platinum differ. */
export interface CommentDay {
  dayMs: number
  platform: string
  kind: string
  sky: number
  ours: number
}

export interface CommentReport {
  lines: CommentLine[] | null
  days: CommentDay[] | null
  skyTotal: number
  oursTotal: number
  skyNotImported: number
  /** Of oursTotal, how many are coded. Absent on an older server. */
  oursCoded?: number
}

/** Where Sky's side was read: Sky's Sprinklr dashboard and its saved widgets (sprinklrrecon.Source). */
export interface ReconSource {
  dashboardId: string
  /** The dashboard's name when known, else "". */
  dashboard: string
  widgets: Array<{ id: string; name: string }>
}

/** How Sky's side was read: from the one-hour cache, or live. Kept in the JSON, not on screen. */
export interface SkyRead {
  readAtMs: number
  cached: boolean
  calls: number
}

export interface ReconWindow {
  from: string
  to: string
  startMs: number
  /** Inclusive: the last millisecond of the last day. */
  endMs: number
}

export interface ReconciliationResponse {
  window: ReconWindow
  sky: SkyRead
  posts: ReconReport
  comments: CommentReport
  settings: CountingSettings
  canEdit: boolean
  partial: boolean
  /** Only a Manager may skip the one-hour cache. */
  canFetchFresh: boolean
  /** How far Platinum's import has got (0 when not known). */
  frontierMs?: number
  source?: ReconSource
}

/** One message on a clicked day that is on one side only, with its reason. */
export interface CommentDiff {
  universalId: string
  reason: string
  permalink?: string
  atMs: number
}

export interface CommentDayResponse {
  date: string
  platform: string
  sky: number
  ours: number
  diffs: CommentDiff[] | null
  frontierMs: number
}

/** The server's sentence, with the HTTP status (400, 422, 429, 502, 503…). */
export class ReconRequestError extends Error {
  status?: number
  constructor(message: string, status?: number) {
    super(message)
    this.name = 'ReconRequestError'
    this.status = status
  }
}

function reconError(err: unknown): ReconRequestError {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined
  return new ReconRequestError(extractErrorMessage(err), status)
}

/** The request the page makes: UTC dates, platforms left out when all are chosen. */
export type ReconRequestParams = Record<string, string>

const URL = `${API_BASE_URL}/sky-social-media/reconciliation`
const KEY = ['sky_social', 'reconciliation'] as const

export function reconciliationKey(params: ReconRequestParams) {
  return [...KEY, params.from ?? '', params.to ?? '', params.platforms ?? ''] as const
}

async function fetchReconciliation(params: ReconRequestParams): Promise<ReconciliationResponse> {
  try {
    return (await axios.get<ReconciliationResponse>(URL, { params })).data
  } catch (err) {
    throw reconError(err)
  }
}

/**
 * Sky's Sprinklr numbers beside ours for one UTC range. Every miss asks
 * Sprinklr live (the server caches its answer for an hour), so a failure is
 * never retried on its own: a retry spends Sprinklr calls.
 */
/** Asks only once `enabled`: the page waits for Apply, so opening it never spends Sprinklr calls. */
export function useSkyReconciliation(params: ReconRequestParams, enabled: boolean) {
  const account = useAccount()
  return useQuery({
    queryKey: reconciliationKey(params),
    enabled: enabled && Boolean(account.user),
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () => fetchReconciliation(params),
  })
}

/** A Manager's "Read Sprinklr again": skips the server's cache and replaces this range's answer. */
export function useFetchFreshReconciliation(params: ReconRequestParams) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => fetchReconciliation({ ...params, fresh: '1' }),
    onSuccess: (data) => qc.setQueryData(reconciliationKey(params), data),
  })
}

/** One platform's messages on one UTC day, with a reason for each difference. Fetched only when asked. */
export function useSkyReconciliationCommentDay(date: string, platform: string, enabled: boolean) {
  const account = useAccount()
  return useQuery({
    queryKey: [...KEY, 'comment-day', date, platform],
    enabled: enabled && Boolean(account.user) && Boolean(date) && Boolean(platform),
    staleTime: 10 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<CommentDayResponse> => {
      try {
        return (await axios.get<CommentDayResponse>(`${URL}/comments/day`, { params: { date, platform } })).data
      } catch (err) {
        throw reconError(err)
      }
    },
  })
}

export function useSetCountingSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (next: { postsInSprinklrTable: boolean; countAsSprinklrTable: boolean }) => {
      try {
        return (await axios.put<CountingSettings>(`${URL}/settings`, next)).data
      } catch (err) {
        throw new Error(extractErrorMessage(err))
      }
    },
    // Every dashboard number may change with a switch, so every Sky query is
    // refetched, not just this page's.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sky_social'] }),
  })
}
